// Enhanced CSRF token extraction with debugging
export const getCSRFTokenWithDebug = () => {
  const debugInfo = {
    cookieString: document.cookie,
    cookieLength: document.cookie.length,
    tokens: [],
    errors: []
  };

  try {
    // Get all CSRF tokens
    const allCSRFCookies = getAllCSRFCookies();
    
    // If we have multiple CSRF tokens, clean up old ones
    if (allCSRFCookies.length > 1) {
      cleanupDuplicateCSRFCookies(allCSRFCookies);
    }

    // Method 1: Regex extraction (current method)
    const regex1 = /(?:^|;\s*)csrf-token=([^;]*)/;
    const match1 = document.cookie.match(regex1);
    if (match1) {
      debugInfo.tokens.push({
        method: 'regex1',
        token: decodeURIComponent(match1[1]),
        raw: match1[1]
      });
    }

    // Method 2: Split and iterate
    const cookies = document.cookie.split(';');
    cookies.forEach(cookie => {
      const trimmed = cookie.trim();
      if (trimmed.startsWith('csrf-token=')) {
        const value = trimmed.substring('csrf-token='.length);
        debugInfo.tokens.push({
          method: 'split',
          token: decodeURIComponent(value),
          raw: value
        });
      }
    });

    // Method 3: Using a more lenient regex
    const regex2 = /csrf-token=([^;]+)/;
    const match2 = document.cookie.match(regex2);
    if (match2) {
      debugInfo.tokens.push({
        method: 'regex2',
        token: decodeURIComponent(match2[1]),
        raw: match2[1]
      });
    }

    // Check if all methods agree
    const uniqueTokens = [...new Set(debugInfo.tokens.map(t => t.token))];
    debugInfo.tokensAgree = uniqueTokens.length <= 1;
    debugInfo.uniqueTokenCount = uniqueTokens.length;

    // Return the token from the most reliable method
    if (debugInfo.tokens.length > 0) {
      return {
        token: debugInfo.tokens[0].token,
        debug: debugInfo
      };
    }

    debugInfo.errors.push('No CSRF token found in cookies');
    return { token: null, debug: debugInfo };

  } catch (error) {
    debugInfo.errors.push(error.message);
    return { token: null, debug: debugInfo };
  }
};

// Function to check if cookies are enabled and accessible
export const checkCookieAccess = () => {
  try {
    // Try to set a test cookie
    document.cookie = 'test-cookie=test; path=/';
    const hasTestCookie = document.cookie.includes('test-cookie=test');
    
    // Clean up
    document.cookie = 'test-cookie=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    
    return {
      cookiesEnabled: hasTestCookie,
      cookieString: document.cookie,
      cookieCount: document.cookie.split(';').filter(c => c.trim()).length
    };
  } catch (error) {
    return {
      cookiesEnabled: false,
      error: error.message
    };
  }
};

// Function to get all CSRF-related cookies
export const getAllCSRFCookies = () => {
  const cookies = document.cookie.split(';');
  const csrfCookies = [];
  
  cookies.forEach(cookie => {
    const trimmed = cookie.trim();
    if (trimmed.includes('csrf')) {
      const [name, value] = trimmed.split('=');
      csrfCookies.push({
        name: name.trim(),
        value: value ? value.trim() : '',
        decoded: value ? decodeURIComponent(value.trim()) : ''
      });
    }
  });
  
  return csrfCookies;
};

// Function to clean up duplicate CSRF cookies
export const cleanupDuplicateCSRFCookies = (csrfCookies) => {
  // If we don't have multiple cookies, nothing to clean
  if (!csrfCookies || csrfCookies.length <= 1) return;

  // Keep the first CSRF token (most recent)
  const keepToken = csrfCookies[0].value;

  // Clear all CSRF cookies first
  csrfCookies.forEach(cookie => {
    // Clear cookie with various path combinations to ensure removal
    document.cookie = `${cookie.name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    document.cookie = `${cookie.name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
    document.cookie = `${cookie.name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
  });

  // Set back the single CSRF token
  const secure = window.location.protocol === 'https:' ? '; secure' : '';
  document.cookie = `csrf-token=${keepToken}; path=/; samesite=lax${secure}`;

  // Log cleanup action in development
  if (process.env.NODE_ENV === 'development') {
    console.debug('Cleaned up duplicate CSRF cookies', {
      removed: csrfCookies.length - 1,
      kept: keepToken.substring(0, 8) + '...'
    });
  }
};
